import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Logo } from '@/components/brand/Logo'
import { Button } from '@/components/ui/Button'

export function SignupPage() {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'sent' | 'error'>('idle')
  const [devLink, setDevLink] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.includes('@')) return
    setStatus('loading')
    try {
      const res = await fetch('/api/auth/magic-link/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, intent: 'signup' }),
      })
      const data = await res.json() as { ok: boolean; message: string; dev_link?: string; error?: string }
      if (data.ok) {
        setStatus('sent')
        if (import.meta.env.DEV && data.dev_link) setDevLink(data.dev_link)
      } else {
        setStatus('error')
        setErrorMsg(data.error ?? 'Something went wrong. Please try again.')
      }
    } catch {
      setStatus('error')
      setErrorMsg('Could not connect. Please try again.')
    }
  }

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <div className="p-6">
        <Link to="/"><Logo size="sm" /></Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-sm">
          {status === 'sent' ? (
            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-echo-mark/10 rounded-full flex items-center justify-center mx-auto">
                <span className="text-echo-mark text-xl font-medium">✓</span>
              </div>
              <h1 className="text-2xl font-medium text-deep-ink">Check your email</h1>
              <p className="text-slate-500 text-sm">
                We sent a link to <strong className="text-deep-ink">{email}</strong>.
                Click it to set up your workspace.
              </p>
              {devLink && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-left">
                  <p className="text-xs font-medium text-amber-700 mb-1">Dev mode — magic link:</p>
                  <a href={devLink} className="text-xs text-amber-600 hover:underline break-all">{devLink}</a>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h1 className="text-2xl font-medium text-deep-ink mb-2">Start for free</h1>
                <p className="text-slate-500 text-sm">No credit card required. Set up your workspace in 2 minutes.</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-deep-ink mb-1.5" htmlFor="signup-email">
                    Work email
                  </label>
                  <input
                    id="signup-email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@yourcompany.com"
                    required
                    autoFocus
                    className="w-full px-4 py-2.5 border border-border-gray rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-echo-mark/30 focus:border-echo-mark bg-white placeholder:text-slate-400"
                  />
                </div>
                {status === 'error' && (
                  <p className="text-sm text-risk-rose">{errorMsg}</p>
                )}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  disabled={status === 'loading' || !email.includes('@')}
                >
                  {status === 'loading' ? 'Sending…' : 'Get your magic link →'}
                </Button>
                {!email.includes('@') && (
                  <p className="text-xs text-ink/50 text-center">Enter a valid work email to enable.</p>
                )}
              </form>
              <p className="text-center text-sm text-slate-500 mt-6">
                Already have an account?{' '}
                <Link to="/signin" className="text-echo-mark hover:underline font-medium">Sign in</Link>
              </p>
              <p className="text-center text-xs text-slate-400 mt-8 leading-relaxed">
                By signing up you agree to our Terms of Service and Privacy Policy.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
